import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { usePdfThumbnails, PdfPageDialog } from '../PdfThumbnailRenderer';
import { useChunkEditor } from './hooks/useChunkEditor';
import { useChunkVersions } from './hooks/useChunkVersions';
import { usePageSelection } from './hooks/usePageSelection';
import { pdfToBookPage } from './utils/chunkUtils';
import EditorHeader from './components/EditorHeader';
import PageGrid from './components/PageGrid';
import ChunkList from './components/ChunkList';
import ChunkDetailsPanel from './components/ChunkDetailsPanel';
import CopyrightHolderStep from './components/CopyrightHolderStep';
import FixedBottomNavigation from './components/FixedBottomNavigation';
import VersionSelector from './components/VersionSelector';

export default function ChunkEditor({ classification, pdfFile, onSave, onCancel }) {
  // Get EAN from classification metadata for version lookup
  const ean = classification?.document?.metadata?.ean;

  const {
    activeStep,
    setActiveStep,
    localChunks,
    setLocalChunks,
    localMetadata,
    setLocalMetadata,
    validationError,
    setValidationError,
    saving,
    setSaving,
    selectedChunkId,
    setSelectedChunkId,
    selectedPages,
    setSelectedPages,
    selectedPageForDialog,
    setSelectedPageForDialog,
    lastSelectedPage,
    setLastSelectedPage,
    thumbnailRefs,
    validateAndSetError,
    overlapWarning,
    handleUpdateChunkField,
    getPageChunkInfo,
    loadVersionChunks,
    resetToClassification,
  } = useChunkEditor(classification);

  const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);

  // Version history management
  const {
    versions,
    selectedVersion,
    isLoading: isLoadingVersions,
    hasVersions,
    fetchVersions,
    selectVersion,
    clearSelection,
  } = useChunkVersions(ean);

  // Fetch versions on mount if EAN exists
  useEffect(() => {
    if (ean) {
      fetchVersions();
    }
  }, [ean, fetchVersions]);

  // Load version data when a version is selected
  useEffect(() => {
    if (selectedVersion) {
      loadVersionChunks(selectedVersion);
    }
  }, [selectedVersion, loadVersionChunks]);

  // Handle clearing the selected version
  const handleClearVersion = useCallback(() => {
    clearSelection();
    resetToClassification();
  }, [clearSelection, resetToClassification]);

  const { handlePageClick } = usePageSelection(
    setSelectedPages,
    lastSelectedPage,
    setLastSelectedPage
  );

  const { fileUrl, numPages, generateThumbnail, pdfDoc } = usePdfThumbnails(pdfFile);

  const totalPages = numPages || (typeof classification.document === 'object'
    ? classification.document.page_count
    : classification.document);

  const copyrightHolders = classification?.document?.metadata?.copyright_holders || {};

  const handleSave = async () => {
    if (!validateAndSetError()) return;
    setSaving(true);
    try {
      await onSave({ chunks: localChunks, metadata: localMetadata });
    } catch (err) {
      console.error('Save failed:', err);
      setValidationError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!validateAndSetError()) return;
      if (overlapWarning) {
        setOverlapDialogOpen(true);
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleOverlapConfirm = () => {
    setOverlapDialogOpen(false);
    setActiveStep((prev) => prev + 1);
  };

  const handleOverlapCancel = () => {
    setOverlapDialogOpen(false);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreateChunk = () => {
    if (selectedPages.length === 0) return;
    const minPdfPage = Math.min(...selectedPages);
    const maxPdfPage = Math.max(...selectedPages);
    const bookStart = pdfToBookPage(minPdfPage, localMetadata.page_offset);
    const bookEnd = pdfToBookPage(maxPdfPage, localMetadata.page_offset);

    const newChunk = {
      id: `chunk-${Date.now()}`,
      startPage: bookStart,
      endPage: bookEnd,
      name: `Chunk ${localChunks.length + 1}`,
      originalChunkName: `${classification.filename.split('.')[0]}-${String(
        bookStart
      ).padStart(3, '0')}`,
      visibility: 'normal',
      copyright_holders: [],
    };

    setLocalChunks([...localChunks, newChunk].sort((a, b) => a.startPage - b.startPage));
    setSelectedChunkId(newChunk.id);
    setSelectedPages([]);
  };

  const handleRemoveChunk = (chunkId) => {
    if (localChunks.length <= 1) {
      return setValidationError('Cannot remove the last chunk');
    }
    setLocalChunks(localChunks.filter((c) => c.id !== chunkId));
    if (selectedChunkId === chunkId) {
      setSelectedChunkId(localChunks[0]?.id || null);
    }
  };

  const selectedChunk = localChunks.find((c) => c.id === selectedChunkId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', width: '100%' }}>
      {/* Fixed Header */}
      <Box sx={{ flexShrink: 0 }}>
        <EditorHeader
          classification={classification}
          totalPages={totalPages}
          pageOffset={localMetadata.page_offset}
          onBack={onCancel}
        />

        {/* Version Selector - only shown when versions exist */}
        {hasVersions && (
          <VersionSelector
            versions={versions}
            selectedVersionId={selectedVersion?.id}
            isLoading={isLoadingVersions}
            onSelectVersion={selectVersion}
            onClearSelection={handleClearVersion}
          />
        )}
      </Box>

      {/* Validation Error Alert */}
      {validationError && (
        <Box sx={{ flexShrink: 0 }}>
          <Alert severity="error" sx={{ m: 2, mx: 3 }}>
            {validationError}
          </Alert>
        </Box>
      )}

      {/* Overlap Warning Alert (step 0 only, non-blocking) */}
      {activeStep === 0 && overlapWarning && (
        <Box sx={{ flexShrink: 0 }}>
          <Alert severity="warning" sx={{ m: 2, mx: 3 }}>
            {overlapWarning}
          </Alert>
        </Box>
      )}

      {/* Content Area - scrollable container */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', bgcolor: 'background.default', p: 3 }}>
        {activeStep === 0 ? (
          <Box sx={{ display: 'flex', flex: 1, gap: 3, overflow: 'hidden' }}>
            <PageGrid
              totalPages={totalPages}
              pageOffset={localMetadata.page_offset}
              generateThumbnail={generateThumbnail}
              getPageChunkInfo={getPageChunkInfo}
              selectedPages={selectedPages}
              selectedChunk={selectedChunk}
              onPageClick={handlePageClick}
              onZoomClick={setSelectedPageForDialog}
              onCreateChunk={handleCreateChunk}
              thumbnailRefs={thumbnailRefs}
            />
            <ChunkList
              chunks={localChunks}
              selectedChunkId={selectedChunkId}
              onSelectChunk={setSelectedChunkId}
              onRemoveChunk={handleRemoveChunk}
            />
            <ChunkDetailsPanel
              selectedChunk={selectedChunk}
              localMetadata={localMetadata}
              onUpdateChunkField={handleUpdateChunkField}
              onMetadataChange={setLocalMetadata}
            />
          </Box>
        ) : (
          <CopyrightHolderStep
            chunks={localChunks}
            copyrightHolders={copyrightHolders}
            onUpdateChunks={setLocalChunks}
            fileUrl={fileUrl}
            numPages={numPages}
          />
        )}
      </Box>

      {/* Bottom Navigation */}
      <Box sx={{ flexShrink: 0 }}>
        <FixedBottomNavigation
          step={activeStep === 0 ? 'edit' : 'copyright'}
          onBack={activeStep === 1 ? handleBack : undefined}
          onNext={activeStep === 0 ? handleNext : undefined}
          onCancel={onCancel}
          onSave={activeStep === 1 ? handleSave : undefined}
          saving={saving}
        />
      </Box>

      {selectedPageForDialog && (
        <PdfPageDialog
          open={Boolean(selectedPageForDialog)}
          onClose={() => setSelectedPageForDialog(null)}
          fileUrl={fileUrl}
          pdfDoc={pdfDoc}
          pageNumber={selectedPageForDialog}
        />
      )}

      <Dialog open={overlapDialogOpen} onClose={handleOverlapCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Overlapping Chunks Detected</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The following chunks have overlapping page ranges:
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {overlapWarning}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Overlapping chunks will both be included in the output. Each overlapping
            page will appear in multiple chunks. Do you want to proceed anyway?
            Are the chunk identifiers set correctly (suffix = start page)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverlapCancel} variant="outlined">Cancel</Button>
          <Button onClick={handleOverlapConfirm} variant="contained" color="warning">
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}